import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';

import {size, isEmpty} from 'lodash';

import {getPercent} from '@ey/utils';

import {labels} from '../../../../../../util/utils';
import Utility from '../../../../../../util/utilityfunctions';

import FilterChips from '../../../../../Common/FilterChips';
import BadgeLabel from '../../../../../Common/Badge/BadgeLabel';
import EmptyMessage from '../../../../../Common/Messages/EmptyMessage';
import MetricsTable from '../../../../../Common/MetricsTable';

import Search from '../../../../../CoreWrappers/Search';

const CCPEngagementTable = ({engagementMetrics}) => {
	const [engagementData, setEngagementData] = useState([]);
	const [showMessage, setShowMessage] = useState(false);
	const openGroups = useRef([]);

	const dashboardContext = useSelector((store) => store.dashboardContext);
	const numberFormat = useSelector((store) => store.userPreferences.numberFormat);
	const dashboardCCPFilters = useSelector(({dashboardFilters}) => dashboardFilters.ccp);
	const hasEngagementFiltersApplyed = dashboardCCPFilters !== null;

	useEffect(() => {
		buildTable();
	}, [engagementMetrics, dashboardCCPFilters]);

	const getRequestsElement = (partial, total) => {
		const value =
			total && partial && `${partial} (${Utility.getFormattedNumber(numberFormat, getPercent(partial, total))}%)`;

		return <span className="ellipsis-one-line">{value}</span>;
	};

	const buildTable = () => {
		// check if selected entities have engagements
		if (dashboardContext.entities?.every((entity) => entity.engagements?.length === 0)) {
			setShowMessage(true);
		} else {
			let data = [];

			// show entities base on org chart order and if it was selected
			dashboardContext.orgChart.forEach((node) => {
				loadNodes(node, dashboardContext.entities, data);
			});

			setShowMessage(false);

			setEngagementData(data);
		}
	};

	const loadNodes = (node, entities, data) => {
		// check if entity is selected
		let entity = entities.find((x) => x.id === node.id);

		if (entity && entity.engagements?.length > 0) {
			const items = getEngagements(entity);
			let group = {
				id: entity.id,
				name: entity.label,
				isOpen: isGroupOpened(entity.id),
				items,
				customGroupRender: () => {
					let result = <BadgeLabel label={entity.label} value={items.length} />;

					return result;
				}
			};

			group.engCount = group.items?.length;

			if (entity.isEnabled) {
				data.push(group);
			}
		}

		// load children entities for each node
		node.nodes.forEach((n) => {
			loadNodes(n, entities, data);
		});
	};

	const isGroupOpened = (id) => {
		return openGroups.current.some((x) => x === id);
	};

	const getEngagements = (entity) => {
		let engagements = entity.engagements.reduce((filtered, engagementId) => {
			const engagement = engagementMetrics.find((x) => x.engId === engagementId);

			if (engagement) {
				filtered.push(engagement);
			}

			return filtered;
		}, []);

		if (hasEngagementFiltersApplyed) {
			engagements = engagements.filter((engId) => dashboardCCPFilters?.engagements?.includes(engId));
		}

		return engagements;
	};

	const isSearch = () => {
		return hasEngagementFiltersApplyed;
	};

	const isGroupOpen = (id) => {
		return openGroups.current.includes(id);
	};

	const noResultFoundMessage = isSearch() ? (
		<EmptyMessage title={labels.noResultsFound} />
	) : (
		<EmptyMessage title={labels.thereIsNoDataToDisplay} subTitle={labels.selectadditionalEntitiesToContinue} />
	);

	const handlerOnGroupChange = (group, isOpen) => {
		if (isOpen) {
			openGroups.current.push(group.id);
		} else {
			openGroups.current = openGroups.current.filter((x) => x !== group.id);
		}
	};

	const renderCustomDetails = () => {
		if (dashboardCCPFilters?.filters) {
			const filterChipsProps = {
				filters: [...dashboardCCPFilters?.filters],
				onClearFilter: dashboardCCPFilters?.onClearFilter
			};

			return <FilterChips {...filterChipsProps} />;
		}
	};

	const renderNewTable = () => {
		const tableProps = {
			data: engagementData,
			variant: 'ccp',
			renderFilter: renderCustomDetails
		};

		return <MetricsTable {...tableProps} />;
	};

	return <>{renderNewTable()}</>;
};

CCPEngagementTable.defaultProps = {
	engagementMetrics: []
};

CCPEngagementTable.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool
		})
	)
};

export default CCPEngagementTable;
